import { IBadgeList } from '@faire/web-api--source/indigofair/badges/IBadgeList';
import { FreeShippingReason } from '@faire/web-api--source/indigofair/data/FreeShippingReason';
import { IApplicableMembershipBenefits } from '@faire/web-api--source/indigofair/data/IApplicableMembershipBenefits';
import { IBrandTilePromoLabel } from '@faire/web-api--source/indigofair/data/IBrandTilePromoLabel';
import { IFaireIcon } from '@faire/web-api--source/indigofair/data/IFaireIcon';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IInformationMessage } from '@faire/web-api--source/indigofair/data/IInformationMessage';
import { IInsiderLabel } from '@faire/web-api--source/indigofair/data/IInsiderLabel';

export namespace IBrandTile {
  /**
   * Return an instance of IBrandTile with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IBrandTile>): IBrandTile =>
    ({
      settings_to_assign_on_view: [],
      product_images: [],
      ...(partial ?? {}),
    }) as IBrandTile;
}

export interface IBrandTile {
  /**
   * The brand's token.
   * @protobufOption (.faire.common.tokenType) = BRAND
   */
  token: string | undefined;
  name: string | undefined;
  /**
   * The main image to show in the tile. Sometimes cropping might be necessary, that's left to the client.
   */
  image: IImage | undefined;
  /**
   * @deprecated
   */
  minimum_order_amount_cents: number | undefined;
  minimum_order_amount: IFaireMoney | undefined;
  /**
   * Whether the current customer has favorited this brand. Replaced with `isFollowing`
   * @deprecated
   */
  favorite: boolean | undefined;
  /**
   * Whether the current customer is following the brand
   */
  is_following: boolean | undefined;
  /**
   * If the viewing retailer gets free shipping for a brand based on their current or eligible Insider status,
   * this will be populated with the brand-specific FreeShippingReason.
   * Note that this should NOT be used to determine free shipping at checkout since it will be populated for retailers
   * who have been invited to but have not signed up for Insider.
   * @deprecated
   */
  free_shipping_reason: keyof typeof FreeShippingReason | undefined;
  /**
   * All insider benefits a viewing retailer gets for this brand based on their current or eligible Insider status.
   */
  insider_benefits: IApplicableMembershipBenefits | undefined;
  insider_label: IInsiderLabel | undefined;
  /**
   * Used to display discount matching on mobile during the FWM/FSM.
   */
  brand_promo_string: string | undefined;
  /**
   * Used to display featured discount promo
   * figma: https://www.figma.com/file/qKnG7kRLCcvrn1rVtqYynz/FRA%2FDEU--Local-discount?node-id=377%3A125361
   */
  featured_brand_promo_label: IBrandTilePromoLabel | undefined;
  brand_promo_label: IBrandTilePromoLabel | undefined;
  /**
   * @deprecated
   */
  faire_winter_market_apparel_preorder_special_eligibility: boolean | undefined;
  highest_tier_promo_short_description: string | undefined;
  has_new_products: boolean | undefined;
  /**
   * @deprecated
   */
  loyalty_program_enrolled: boolean | undefined;
  ships_from_country: string | undefined;
  /**
   * isHighlightedBrandForPromotionalEvent is used to highlight brands during a promotional event.
   * This will mirror the behavior used during FWM 2021 in faireWinterMarketApparelPreorderSpecialEligibility,
   * which highlighted apparel preorder brands. For the FSM 2021, this will be used to highlight international brands.
   */
  is_highlighted_brand_for_promotional_event: boolean | undefined;
  /**
   * True if the brand qualifies as a Top Shop for the current cycle
   */
  is_top_shop: boolean | undefined;
  /**
   * True if products on the brand page should be sorted by newest instead of featured
   */
  sort_products_by_newest: boolean | undefined;
  based_in_country: string | undefined;
  /**
   * True if the brand qualifies as a showroom brand
   */
  is_showroom: boolean | undefined;
  /**
   * Where the brand tile is retrieved from a knowledge graph brand. See SMALL_WITH_KNOWLEDE_GRAPH_BRANDS_TILE_CAROUSEL
   * @deprecated
   */
  is_knowledge_graph_brand: boolean | undefined;
  /**
   * Brand's region flag URL if applicable to the retailer.
   * @deprecated
   */
  region_flag_url: string | undefined;
  region_flag_image: IImage | undefined;
  has_insider_free_shipping: boolean | undefined;
  /**
   * Should be consumed only in Brand tiles, referring to `badgeList` field in LeanProductTile for Product tiles.
   */
  badge_list: IBadgeList | undefined;
  /**
   * Formatted and localized avg brand review rating, i.e "4.8" in english or "4,8" in french.
   */
  avg_brand_review_rating: string | undefined;
  settings_to_assign_on_view: Array<string>;
  product_images: Array<IImage>;
  profile_image: IImage | undefined;
  /**
   * Icon to be displayed beside the brand name on product tiles
   */
  icon: IFaireIcon | undefined;
  /**
   * Formatted and localized brand's catalog size
   */
  catalog_size: string | undefined;
  /**
   * @protobufOption (faire.common.diffIgnore) = ALWAYS
   */
  impression_id: string | undefined;
  faire_source_label: IInformationMessage | undefined;
}
